<template>
  <div class="pt-10 pb-10 sm:px-6 lg:px-32">
    <template v-if="loadingNotifications">
      <div class="flex items-center justify-center h-screen bg-gray-type-4">
        <div class="spinner"></div>
      </div>
    </template>
    <template v-else>
      <template v-if="notifications && notifications.length > 0">
        <div class="relative flex flex-wrap h-screen overflow-hidden bg-gray-type-4">
          <div
            class="w-full h-full overflow-y-auto bg-white border rounded rounded-r-none sm:w-2/6"
          >
            <div
              @click="showNotificationDetails(notification)"
              class="flex items-center justify-between px-4 py-5 transition-all duration-200 ease-in-out bg-white border-b cursor-pointer"
              :class="isSelectedItem(notification) ? 'border-l-2' : ''"
              style="border-left-color: #00e069"
              v-for="notification in notifications"
              :key="notification.id"
            >
              <div class="flex items-center">
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32">
                    <template v-if="notification.is_read">
                      <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2z" />
                      <path
                        fill-rule="evenodd"
                        d="M8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z"
                      />
                    </template>
                    <template v-else>
                      <path
                        d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z"
                      />
                    </template>
                  </svg>
                </div>
                <div class="ml-2">
                  <div class="text-sm font-bold text-dark-type-3">{{ notification.title }}</div>
                  <ellipsis
                    class="text-xs font-medium text-dark-type-4"
                    :text="notification.description"
                    :number-of-characters="50"
                  ></ellipsis>
                </div>
              </div>
            </div>
          </div>
          <div class="self-stretch flex-1 hidden sm:block">
            <template v-if="selectedNotification">
              <div class="p-10">
                <div
                  class="text-sm text-gray-800"
                >{{ selectedNotification.created_at | date('D MMMM, YYYY') }}</div>
                <div class="mt-2 text-2xl">{{ selectedNotification.title }}</div>
                <div class="mt-4">
                  <div class="description" v-html="selectedNotification.description"></div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="flex flex-col items-center justify-center h-full">
                <img src="@/assets/img/email-sent.svg" alt />
                <div
                  class="mt-5 text-sm font-medium text-center text-dark-type-4"
                >Click on a notification to view content</div>
              </div>
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col items-center justify-center lg:py-20">
          <img src="@/assets/img/something-went-wrong.svg" alt="No new notification" />
          <div class="mt-10 text-2xl font-bold text-dark-type-3">No new notification</div>
          <div
            class="mt-4 text-xs font-medium text-dark-type-4"
          >You don't have any new notifications. If you do, they'll show up here.</div>
        </div>
      </template>
    </template>

    <div class="fixed inset-0 overflow-hidden sm:hidden" v-if="selectedNotification">
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>
        <section class="absolute inset-x-0 inset-y-0 flex max-w-full">
          <div class="relative w-screen">
            <div class="flex flex-col h-full py-6 space-y-6 overflow-y-scroll bg-white shadow-xl">
              <header class="px-4 sm:px-6">
                <div class="flex pt-4 pr-2 sm:pr-4">
                  <button
                    aria-label="Close panel"
                    class="text-active transition duration-150 ease-in-out"
                    @click="selectedNotification = null"
                  >
                    <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </header>
              <div class="relative flex-1 px-4 sm:px-6">
                <div
                  class="text-sm text-gray-800"
                >{{ selectedNotification.created_at | date('D MMMM, YYYY') }}</div>
                <div class="mt-2 text-2xl">{{ selectedNotification.title }}</div>
                <div class="mt-4">
                  <pre class="description">{{selectedNotification.description}}</pre>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Ellipsis from "@/components/Ellipsis";

export default {
  data() {
    return {
      showDetails: false,
      selectedNotification: null,
      loadingNotifications: false,
      errorLoading: false,
    };
  },
  components: {
    Ellipsis,
  },
  created() {
    if (!this.notifications.length) {
      this.fetchNotifications();
    }
  },
  computed: {
    ...mapGetters("app", ["notifications"]),
    ...mapGetters("auth", ["user"]),
  },
  methods: {
    fetchNotifications(params) {
      this.loadingNotifications = true;
      this.errorLoading = false;

      this.$store
        .dispatch("app/fetchNotifications", { params })
        .catch(() => {
          this.errorLoading = true;
        })
        .finally(() => (this.loadingNotifications = false));
    },
    showNotificationDetails(item) {
      this.selectedNotification = item;

      this.showDetails = true;

      if (!item.is_read) {
        this.$store.dispatch("app/markNotificationAsRead", {
          notificationId: item.id,
        });
      }
    },
    isSelectedItem(item) {
      if (this.selectedNotification) {
        return this.selectedNotification.id === item.id;
      } else {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  white-space: pre-wrap;
  word-break: break-word;
  @apply text-sm font-normal font-sans;

}
</style>